import React, { useEffect, useMemo, useState } from "react";
import { Col, Flex, Row, Select, SelectProps } from "antd";
import { debounce, formatNumberFixed, renderClassNameStatusSir } from "../../../utils/helper";
import { WorkflowServices } from "../../../services/workflow";
import { SearchOutlined } from "@ant-design/icons";
import { ISirTableData, ISirTableDataResponse } from "../../../interfaces/invoice";
import { InvoiceServices } from "../../../services/invoice";

interface SearchSirProps {
  selectProps?: SelectProps;
  onChangeSir: (invoice: ISirTableData) => void;
  invoiceIds: string[];
  supplierId: string;
  isDisabled?: boolean;
  isTypeFlight: boolean;
}

const SearchSir: React.FC<SearchSirProps> = ({
  selectProps,
  onChangeSir,
  invoiceIds,
  supplierId,
  isDisabled = false,
  isTypeFlight,
}) => {
  const [fetching, setFetching] = useState(false);
  const [data, setData] = useState<SelectProps["options"]>([]);
  const [debouncedSearchValue, setDebouncedSearchValue] = useState<string | null>("");
  const [textSearch, setTextSearch] = useState<string | null>();
  const [value, setValue] = useState<string | null>();

  const fetchData = async () => {
    try {
      setFetching(true);
      const params = {
        keyword: debouncedSearchValue || undefined,
        invoiceIds: invoiceIds,
        supplierId: supplierId,
        type: isTypeFlight ? "Flight": "Common"
        
      };
      const response: any = await WorkflowServices.getSearchSir(params);
      const { success = false, data = [] } = response || {};
      if (success) {
        setData(data || []);
      }
    } catch (error) {
      console.error("Fetch suppliers error:", error);
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => {
    if (debouncedSearchValue) {
      fetchData();
    } else setData([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchValue]);

  const handleSearch = (newValue: string) => {
    setFetching(true);
    setTextSearch(newValue);
    debouncedSearch(newValue);
  };

  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        setDebouncedSearchValue(value);
      }, 300),
    []
  );

  const handleChange = async (newValue: string) => {
    const findData = data?.find((d) => d.id === newValue);
    setValue(null);
    setTextSearch(null);
    setDebouncedSearchValue(null);
    try {
      const response: ISirTableDataResponse = await InvoiceServices.getInvoiceDetail(findData?.invoiceId!);
      const { success = false, data } = response || {};
      if (success) {
        onChangeSir({
          ...data,
          supplierInvoiceRecordId: newValue,
        });
      }
    } catch (error) {
      console.error("Fetch Data error:", error);
    }
  };

  const optionRender = (option: any) => {
    return (
      <Row align={"middle"} gutter={12}>
        <Col span={5} className={renderClassNameStatusSir(option.data.status)}>
          {option.data.status}
        </Col>
        <Col span={4}>{option.data.supplierCode}</Col>
        <Col span={5}>{option.data.bookingNo}</Col>
        <Col span={6}>{option.data.leadPax}</Col>
        <Col span={4} style={{ textAlign: "right" }}>
          {formatNumberFixed(option.data.amount)}
        </Col>
      </Row>
    );
  };

  return (
    <Flex align="center" gap={8} justify="space-between" style={{ width: "100%" }}>
      <span>Add Another SIR</span>
      <Select
        {...selectProps}
        value={value}
        placeholder={"Enter Lead Pax Name or Booking Ref ID"}
        showSearch
        defaultActiveFirstOption={false}
        prefix={<SearchOutlined />}
        suffixIcon={!fetching ? null : undefined}
        filterOption={false}
        onSearch={handleSearch}
        onChange={handleChange}
        notFoundContent={textSearch && !fetching && data?.length === 0 ? "No Data" : null}
        options={(data || []).map((d) => ({
          ...d,
          value: d.id,
          label: d.bookingNo + " " + d.leadPax,
        }))}
        optionRender={optionRender}
        className="w-full"
        loading={fetching}
        onBlur={() => {
          setFetching(false);
          setDebouncedSearchValue(null);
          setTextSearch(null);
          setData([]);
        }}
        popupMatchSelectWidth={610}
        placement="bottomLeft"
        disabled={isDisabled}
      />
    </Flex>
  );
};

export default SearchSir;
